import { Case } from '../models/case';
import { CaseUser, CaseUserRole } from '../models/case-user';
import { User } from '../models/user';
import { store } from '../redux/store';
import { caseUserSelectByQuery } from '../redux/thunks/DBServiceThunks';

export type PermissionLevel = 'viewer' | 'editor';
const hasPermission = (userRole: PermissionLevel, requiredRole: PermissionLevel): boolean => {
    const hierarchy: Record<PermissionLevel, number> = {
        viewer: 1,
        editor: 2,
    };

    return hierarchy[userRole] >= hierarchy[requiredRole];
};

export const getUserCasePermission = (
    currentCase: Case | null,
    currentUser: User | null
): PermissionLevel | null => {
    if (!currentCase || !currentUser) {
        return null;
    }

    // Get current user's Stytch member ID
    const currentUserStytchId = currentUser.stytchMemberId;
    if (!currentUserStytchId) {
        return null;
    }

    // Get case-users from Redux store
    const state = store.getState();
    const caseUsers = caseUserSelectByQuery(state.DBCaseUserReducer, { caseId: currentCase._id });

    // Check case-users for access (SINGLE SOURCE OF TRUTH)
    const caseUser = caseUsers.find(
        cu => cu.caseId === currentCase._id && cu.userId === currentUserStytchId
    );

    if (caseUser) {
        return caseUser.role as PermissionLevel;
    }

    return null;
};

export const canPerformAction = (
    currentCase: Case | null,
    currentUser: User | null,
    requiredRole: PermissionLevel
): boolean => {
    const userPermission = getUserCasePermission(currentCase, currentUser);

    if (!userPermission) {
        return false;
    }

    return hasPermission(userPermission, requiredRole);
};

export const canViewCase = (
    currentCase: Case | null,
    currentUser: User | null
): boolean => {
    return canPerformAction(currentCase, currentUser, 'viewer');
};

export const canEditCase = (
    currentCase: Case | null,
    currentUser: User | null
): boolean => {
    return canPerformAction(currentCase, currentUser, 'editor');
};

export const getPermissionLabel = (permission: PermissionLevel | null): string => {
    if (!permission) return 'No Access';

    const labels: Record<PermissionLevel, string> = {
        viewer: 'Viewer',
        editor: 'Editor',
    };

    return labels[permission];
};
